import {
  Action,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import {
  SigninRedirectArgs,
  User,
  UserManager,
  UserManagerSettings,
} from 'oidc-client-ts';
import ConfigUtils from '@/utils/config.utils';
import { TaftKeycloakRoles } from '@/utils/constants';
import store from '..';

const oidcSettings: UserManagerSettings = ConfigUtils.getOidcSettings();

export enum AuthStatus {
  NotAuthenticated,
  AuthenticationStarted,
  AuthenticationFailed,
  AuthenticationSucceeded
}

export const userManager = new UserManager(oidcSettings);

const doLogout = async () => {
  // eslint-disable-next-line no-console
  console.log('Logging out');
  await userManager.removeUser();
  await userManager.revokeTokens();
  userManager.stopSilentRenew();
  userManager.signoutRedirect();
};

userManager.events.addSilentRenewError(async () => {
  // eslint-disable-next-line no-console
  console.log('>> silent renew error >>');
  await doLogout();
});

const routeIsPublic = (route) => {
  if (route.meta && route.meta.isPublic) {
    return true;
  }
  if (route.meta && Array.isArray(route.meta)
    && route.meta.reduce((isPublic, meta) => meta.isPublic || isPublic, false)) {
    return true;
  }
  return false;
};

const routeIsOidcCallback = (route) => {
  if (route.meta && route.meta.isOidcCallback) {
    return true;
  }
  if (route.meta && Array.isArray(route.meta)
    && route.meta.reduce((isOidcCallback, meta) => meta.isOidcCallback || isOidcCallback, false)) {
    return true;
  }
  if (route.path && route.path.replace(/\/$/, '') === oidcSettings.redirect_uri) {
    return true;
  }
  if (route.path && route.path.replace(/\/$/, '') === oidcSettings.silent_redirect_uri) {
    return true;
  }
  return false;
};

@Module({
  store,
  name: 'oidcStore',
  namespaced: true,
  dynamic: true,
})
export default class AuthModule extends VuexModule {
  user?: User = undefined;
  status = AuthStatus.NotAuthenticated;
  errorMessage = '';

  get userName() {
    return this.user?.profile.name || '';
  }

  get userId() {
    return this.user?.profile.sub || '';
  }

  get isIdirUser() {
    return this.user?.profile.identity_provider === 'idir';
  }

  get isAuthenticated() {
    return this.status === AuthStatus.AuthenticationSucceeded;
  }

  get isDeviceOnlyUser() {
    const userRoles: string[] = (this.user?.profile?.roles as string[]) || [];
    const hasWebAppRole: boolean = [
      TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS.toString(),
      TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_REPORTS.toString(),
    ].some((role) => userRoles.includes(role));
    return (this.user?.profile.identity_provider !== 'idir') && !hasWebAppRole;
  }

  @Mutation
  setCurrentUser(user: User) {
    this.user = user;
    this.status = AuthStatus.AuthenticationSucceeded;
  }

  @Mutation
  setClearSession() {
    this.user = undefined;
    this.status = AuthStatus.NotAuthenticated;
  }

  @Mutation
  setError(message: string): void {
    this.status = AuthStatus.AuthenticationFailed;
    this.errorMessage = message;
  }

  @Mutation
  startLogin() {
    this.status = AuthStatus.AuthenticationStarted;
  }

  @Action
  login(loginParam: {
    redirectPath: string,
    options?: SigninRedirectArgs | undefined,
  }) {
    userManager.clearStaleState();
    sessionStorage.removeItem('oidc_active_route');
    if (loginParam.redirectPath) {
      sessionStorage.setItem('oidc_active_route', loginParam.redirectPath);
    }
    this.startLogin();
    userManager.startSilentRenew();
    return userManager.signinRedirect(loginParam.options ?? {});
  }

  @Action
  async renewToken() {
    // eslint-disable-next-line no-console
    console.log('renewToken fn started');
    const user = await userManager.signinSilent();
    // eslint-disable-next-line no-console
    console.log('renewToken fn resp: ', user);
    if (user) {
      this.setCurrentUser(user);
    } else {
      await userManager.revokeTokens();
      this.setClearSession();
    }
  }

  @Action
  async signInCallback() {
    try {
      await userManager.signinRedirectCallback();
      const user = await userManager.getUser();
      if (user) {
        this.setCurrentUser(user);
        return sessionStorage.getItem('oidc_active_route') || '/';
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('signInCallback error');
      // eslint-disable-next-line no-console
      console.log(error);
    }
    return '/';
  }

  // eslint-disable-next-line class-methods-use-this
  @Action
  async logout() {
    await doLogout();
  }

  @Action
  async silentRenew() {
    try {
      await userManager.signinSilentCallback().then((success) => {
        // eslint-disable-next-line no-console
        console.log('silentrenewed', success);
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  }

  @Action
  async checkAccess(to): Promise<boolean> {
    let hasAccess = true;
    try {
      if (routeIsOidcCallback(to)) {
        return hasAccess;
      }
      const user = await userManager.getUser();
      if (!user || user.expired) {
        if (routeIsPublic(to)) {
          await userManager.removeUser();
          this.setClearSession();
          // eslint-disable-next-line no-console
          console.log('try signinSilent: checkAccess');
          userManager.signinSilent();
          // eslint-disable-next-line no-console
          console.log('signinSilent: checkAccess done');
        } else {
          await this.logout();
          this.setClearSession();
          hasAccess = false;
        }
      } else {
        const routeRoles: string[] = to.meta?.roles || [];
        const userRoles: string[] = (user.profile?.roles as string[]) || [];
        if (routeRoles.length) {
          hasAccess = false;
          for (let index = 0; index < routeRoles.length; index += 1) {
            if (userRoles.includes(routeRoles[index])) {
              hasAccess = true;
              break;
            }
          }
        }
        this.setCurrentUser(user);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('checkAccess: ', error);
      hasAccess = false;
    }
    return hasAccess;
  }
}
